import axios from 'axios'
import { FieldValues, Path, UseFormSetError } from 'react-hook-form'

// Pull the first human-readable message out of a nested DRF error node, e.g.
// [{ non_field_errors: ['msg'] }] or { non_field_errors: ['msg'] }. Returns
// null when no string message can be found.
function firstNestedMessage(value: unknown): string | null {
  const node = Array.isArray(value) ? value.find(Boolean) : value
  if (node && typeof node === 'object') {
    for (const inner of Object.values(node as Record<string, unknown>)) {
      if (Array.isArray(inner) && typeof inner[0] === 'string') return inner[0]
      if (typeof inner === 'string') return inner
    }
  }
  return null
}

export function handleFormError<T extends FieldValues>(
  err: Error | null,
  setError: UseFormSetError<T>
): void {
  if (!err || !axios.isAxiosError(err)) return
  const errors = err.response?.data as Record<string, unknown>
  if (!errors || typeof errors !== 'object') return

  Object.entries(errors).forEach(([key, value]) => {
    // Top-level DRF `detail` (dup key, PROTECT, cross-tenant) has no matching
    // form field — surface it as a form-level error instead of a bogus field.
    if (key === 'detail' && typeof value === 'string') {
      setError('root', { message: value })
      return
    }
    // Flat field errors: { field: ['msg'] } or { field: 'msg' } — existing behavior.
    if (typeof value === 'string') {
      setError(key as Path<T>, { message: value })
      return
    }
    if (
      Array.isArray(value) &&
      (value.length === 0 || typeof value[0] === 'string')
    ) {
      setError(key as Path<T>, { message: value[0] as string })
      return
    }
    // Nested serializer errors (bindings[]/fields[] non_field_errors) can't map
    // to a single form field — route to the form-level error.
    const nested = firstNestedMessage(value)
    if (nested) {
      setError('root', { message: nested })
    }
  })
}
